import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { useState, useEffect } from "react";
import axios from "axios";
import config from "../../../../config";

const RfqResponseForm = ({ show, onHide, data, onSaved, defaultDD }) => {
  const [isFullscreen, setIsFullscreen] = useState(window.innerWidth < 991);
  useEffect(() => {
    const handleResize = () => {
      setIsFullscreen(window.innerWidth < 991);
    };

    // Initial check
    handleResize();

    // Listen for window resize
    window.addEventListener('resize', handleResize);

    // Cleanup on unmount
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [form, setForm] = useState({
    expected_delivery_date: defaultDD?.split("T")[0] || '',
    response_notes: "",
    response_method: "",
    status: "pending",
    tax: 0,
    items: [],
  });

  useEffect(() => {
    if (data?.response) {
      // Populate form with existing response
      setForm({
        expected_delivery_date: data.response.expected_delivery_date?.split("T")[0] || defaultDD?.split("T")[0] || '',
        response_notes: data.response.response_notes || "",
        response_method: data.response.response_method || "",
        status: data.response.status || "pending",
        tax: data.response.tax || 0,
        items: data.response.items || [],
      });
    } else if (data?.response?.items?.length === 0 && data?.items?.length > 0) {
      // Pre-fill with RFQ items if no response items exist
      const mappedItems = data.items.map(item => ({
        product: item.product,
        quality: item.quality || "",
        quantity: item.quantity || item.required_quantity || 0,
        quantity_unit: item.quantity_unit || "",
        rate: 0,
        unit_rate: 0,
        total_price: 0,
      }));

      setForm(prev => ({ ...prev, items: mappedItems }));
    }
  }, [data, defaultDD]);

  const handleFormChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const items = [...form.items];
    items[index][field] = value;

    const qty = parseFloat(items[index].quantity || 0);
    const rate = parseFloat(items[index].unit_rate || 0);
    items[index].total_price = +(qty * rate).toFixed(2);

    setForm(prev => ({ ...prev, items }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const payload = {
      rfq: data.rfq || data.rfq_id,
      rfq_recipient: data._id,
      ...form,
    };

    const url = data.response._id
      ? `${config.API_BASE_URL}/rfq-responses/${data.response._id}`
      : `${config.API_BASE_URL}/rfq-responses`;
    const method = data.response._id ? "put" : "post";

    try {
      await axios[method](url, payload);
      onSaved();
      onHide();
    } catch (err) {
      console.error("Submit Error:", err);
    }
  };

  const handleStatusChange = async newStatus => {
    try {
      await axios.put(`${config.API_BASE_URL}/rfq-responses/${data.response._id}/status`, {
        status: newStatus,
      });
      onSaved();
      onHide();
    } catch (err) {
      console.error("Status Update Error:", err);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl" fullscreen={isFullscreen}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{data.response ? "Edit Quotation" : "Submit Quotation"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-2">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Expected Delivery Date</Form.Label>
                <Form.Control
                  type="date"
                  name="expected_delivery_date"
                  value={form.expected_delivery_date}
                  onChange={handleFormChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Response Method</Form.Label>
                <Form.Select
                  name="response_method"
                  value={form.response_method}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">Select</option>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="portal">Portal</option>
                  <option value="manual">Manual</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Tax (%)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="tax"
                  value={form.tax}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              name="response_notes"
              value={form.response_notes}
              onChange={handleFormChange}
              rows={2}
            />
          </Form.Group>



          <hr />
          <h5 className="mb-3">Product Responses</h5>
          {form.items.map((item, idx) => (

            <Row key={idx} className="mb-4 align-items-end g-2 shadow-sm border rounded p-2 pt-0 pb-3 bg-primary-subtle">
              <Col md={3}>
                <Form.Label>Product</Form.Label>
                <Form.Control
                  type="text"
                  value={
                    data.items?.find(p => (p.product._id || p.product) === item.product)?.product?.productName ||
                    data.products?.find(p => p._id === item.product)?.productName ||
                    "Unknown"
                  }
                  readOnly
                />
              </Col>
              <Col md={2}>
                <Form.Label>Quality</Form.Label>
                <Form.Control
                  type="text"
                  value={item.quality}
                  onChange={e => handleItemChange(idx, "quality", e.target.value)}
                />
              </Col>
              <Col md={2} className="position-relative">
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  value={item.quantity}
                  onChange={e => handleItemChange(idx, "quantity", e.target.value)}
                />
                <div className="quantity-unit-label" style={{
                  position: 'absolute',
                  top: '32px',
                  right: '10px',
                  background: '#f0f0f0',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  fontSize: '0.75rem',
                  color: '#555',
                }}>
                  {item.quantity_unit}
                </div>
              </Col>
              <Col md={1} className="d-none">
                <Form.Label>Unit</Form.Label>
                <Form.Control
                  type="text"
                  value={item.quantity_unit}
                  onChange={e => handleItemChange(idx, "quantity_unit", e.target.value)}
                />
              </Col>
              <Col md={2}>
                <Form.Label>Unit Rate</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={item.unit_rate}
                  onChange={e => handleItemChange(idx, "unit_rate", e.target.value)}
                />
              </Col>
              <Col md={2}>
                <Form.Label>Total</Form.Label>
                <Form.Control
                  type="text"
                  value={item.total_price}
                  readOnly
                />
              </Col>
            </Row>
          ))}
        </Modal.Body >

        <Modal.Footer className="d-flex justify-content-between">
          {data.response && (
            <div className="d-flex gap-2">
              <Button
                variant="outline-success"
                onClick={() => handleStatusChange("approved")}
              >
                Approve
              </Button>
              <Button
                variant="outline-danger"
                onClick={() => handleStatusChange("rejected")}
              >
                Reject
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => handleStatusChange("hold")}
              >
                Hold
              </Button>
            </div>
          )}

          <Button type="submit" variant="primary">
            {data.response ? "Update" : "Submit"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default RfqResponseForm;
